/**
 * HrV1Console.tsx
 *
 * Single-page HR admin console for hr_v1 cycles. Drives every step of the
 * 7-step canonical flow from one screen. Each step renders as a card with
 * status summary + action button(s) that call the corresponding service.
 *
 * Phase-1 ergonomics: JSON paste for employee lists & 360 panels. Phase-2
 * should replace those with directory-pickers and richer forms.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { routes } from '../routes';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import WorkflowStepper from '../components/epr/WorkflowStepper';
import PendingList, { type PendingListItem } from '../components/epr/PendingList';
import EmployeeFinder from '../components/epr/EmployeeFinder';
import { fetchDirectory } from '../services/directoryService';
import type { DirectoryEmployee } from '../types/magicLink';
import {
  findExtremeScores, detectManagerBias,
  type ExtremeScoreRow, type ManagerRatingStat,
} from '../services/calibrationBiasReducers';
import DistributionPackage from '../components/epr/DistributionPackage';
import type { DistributionLink } from '../components/epr/DistributionPackage';
import PanelBuilder from '../components/epr/PanelBuilder';
import { deriveSteps } from '../services/hrV1StepDerivation';
import { listOutcomesForCycle } from '../services/outcomeService';
import AppraisalSummaryView from '../components/epr/AppraisalSummaryView';
import { useToast } from '../components/ui/Toast';

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

import type { ReviewCycle } from '../types';
import type { EPRSubmission } from '../types/eprFramework';
import type { AppraisalForm } from '../types/appraisal';
import type { AppealRecord, OutcomeType } from '../types/appeal';
import type { CalibrationSession, CalibrationBand } from '../types/calibration';
import { CALIBRATION_BANDS, CALIBRATION_BAND_LABELS, BELL_CURVE_TARGETS } from '../types/calibration';

import {
  launchStep2,
  type LaunchStep2Input,
} from '../services/hrCycleLauncher';
import { listHrV1Cycles } from '../services/hrV1CycleService';
import {
  listSubmissionsForCycle,
} from '../services/eprSubmissionService';
import {
  consolidateCycle,
} from '../services/consolidationService';
import {
  createSession, computeAndStoreDistribution, lockSession, getSession,
} from '../services/calibrationService';
import {
  listAppraisalsForCycle, sendToEmployee, updateManagerNotes,
  setDevelopmentPlan,
} from '../services/appraisalService';
import {
  listOpenAppealsForCycle, decideAppeal,
} from '../services/appealService';
import {
  recordOutcome, closeCycle,
} from '../services/outcomeService';
import { advanceCyclesIfDue } from '../services/stepAdvancer';
import { resetCycleData } from '../services/hrCycleReset';

// ─── Page ───────────────────────────────────────────────────────────

const HrV1Console: React.FC = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { user } = useAuth();
  const [cycle, setCycle] = useState<ReviewCycle | null>(null);
  const [submissions, setSubmissions] = useState<EPRSubmission[]>([]);
  const [appraisals, setAppraisals] = useState<AppraisalForm[]>([]);
  const [appeals, setAppeals] = useState<AppealRecord[]>([]);
  const [session, setSession] = useState<CalibrationSession | null>(null);
  const [outcomeCount, setOutcomeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!cycleId) {
      setLoading(false);
      return;
    }
    let alive = true;
    void (async () => {
      // Two-phase load. Cycle doc FIRST, then side queries — so a
      // missing-cycle case doesn't trigger 5 unrelated permission
      // checks (each capable of toasting). Class fix per
      // ENGINEERING.md §0: previously a stale-id navigation would
      // pop a "Missing or insufficient permissions" toast even though
      // the real root cause was "cycle doesn't exist."
      try {
        const cs = await getDoc(doc(db, 'reviewCycles', cycleId));
        if (!alive) return;
        if (!cs.exists()) {
          // Render not-found recovery, no side queries, no toast.
          setLoading(false);
          return;
        }
        setCycle({ id: cs.id, ...(cs.data() as Omit<ReviewCycle, 'id'>) });
      } catch (e) {
        // Real auth / rules failure on the cycle doc itself.
        if (alive) {
          toast.show(`Cannot read cycle: ${(e as Error).message}`, { icon: 'error' });
          setLoading(false);
        }
        return;
      }

      // Side data — failures here are independent (e.g. user might be
      // HR but not have read access to a sibling collection). Catch
      // PER-QUERY so a partial load still renders, and the toast names
      // which collection failed instead of spamming the cycle id.
      try {
        const [subs, apps, openApp, sess, outcomes] = await Promise.all([
          listSubmissionsForCycle(cycleId),
          listAppraisalsForCycle(cycleId),
          listOpenAppealsForCycle(cycleId),
          getSession(cycleId),
          listOutcomesForCycle(cycleId),
        ]);
        if (!alive) return;
        setSubmissions(subs);
        setAppraisals(apps);
        setAppeals(openApp);
        setSession(sess);
        setOutcomeCount(outcomes.length);
      } catch (e) {
        if (alive) {
          toast.show(`Some cycle data could not load: ${(e as Error).message}`, { icon: 'error' });
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cycleId, refreshTick, toast]);

  const refresh = useCallback(() => setRefreshTick(t => t + 1), []);

  // ── Wizard mode — URL query param ?step=N drives focused view ──
  // IMPORTANT: every hook (useCallback / useState / etc) must run on EVERY
  // render in the same order. Keep all hook calls BEFORE the early returns
  // below — otherwise React throws error #310 ("Rendered more hooks than
  // during the previous render") when cycle goes from null → loaded.
  const focusStep = useCallback((num: number) => {
    if (num < 1 || num > 7) return;
    setSearchParams({ step: String(num) }, { replace: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setSearchParams]);

  const backToOverview = useCallback(() => {
    setSearchParams({}, { replace: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setSearchParams]);

  // Early-return terminal states — must come AFTER all hooks.
  if (loading) return <CenterMessage tone="info" title="Loading cycle..." />;
  if (!cycle || !cycleId) {
    // Don't strand the user on a dead-end. Show available cycles inline
    // so a stale URL recovers in one click — no need to bounce through
    // /cycles. ENGINEERING.md §0: fix the symptom + fix the class
    // (any time a stale id reaches this page, user keeps moving).
    return <CycleNotFoundRecovery missingId={cycleId} />;
  }
  if (cycle.frameworkVersion !== 'hr_v1') {
    return (
      <CenterMessage
        tone="warning"
        title="Not an hr_v1 cycle"
        body="This console only handles cycles with frameworkVersion='hr_v1'. Open the legacy OPTICS pages for other cycles."
        action={
          <Link to={routes.cyclesList()}>
            <Button variant="secondary" icon="arrow_back">Back to Cycles</Button>
          </Link>
        }
      />
    );
  }

  const step = cycle.currentStep ?? 0;
  const stats = computeStats(submissions, appraisals, appeals);

  const workflowSteps = deriveSteps({
    currentStep: cycle.currentStep ?? null,
    submissions, appraisals, session,
    openAppeals: appeals,
    outcomeCount,
  });

  const stepParam = Number(searchParams.get('step'));
  const focusedStep = stepParam >= 1 && stepParam <= 7 ? stepParam : null;

  const renderStepCard = (num: number) => {
    switch (num) {
      case 1: return <Step1Card cycle={cycle} submissions={submissions} stats={stats} onAfter={refresh} />;
      case 2: return <Step2Card cycle={cycle} submissions={submissions} onAfter={refresh} />;
      case 3: return <Step3Card cycle={cycle} submissions={submissions} appraisals={appraisals} onAfter={refresh} />;
      case 4: return <Step4Card cycle={cycle} session={session} submissions={submissions} userId={user?.uid ?? 'unknown'} onAfter={refresh} />;
      case 5: return <Step5Card cycle={cycle} appraisals={appraisals} onAfter={refresh} />;
      case 6: return <Step6Card cycle={cycle} appeals={appeals} userId={user?.uid ?? 'unknown'} onAfter={refresh} />;
      case 7: return <Step7Card cycle={cycle} appraisals={appraisals} userId={user?.uid ?? 'unknown'} onAfter={refresh} />;
      default: return null;
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-stone-500">
            {focusedStep
              ? <button onClick={backToOverview} className="hover:text-stone-700 inline-flex items-center gap-1">
                  <span className="material-symbols-rounded" style={{ fontSize: 14 }}>arrow_back</span>
                  Performance Review · Overview
                </button>
              : 'Performance Review'}
          </div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight truncate">{cycle.name}</h1>
          <p className="text-sm text-stone-600 mt-1">
            Status: <b>{cycle.status}</b> · Step <b className="tabular-nums">{step || '—'}</b> of 7
          </p>
        </div>
        <Button variant="secondary" icon="refresh" onClick={refresh}>Refresh</Button>
      </header>

      {/* Workflow visualization — clicking a node enters wizard mode for that step */}
      <section className="rounded-xl border border-stone-200 bg-white px-4 py-5">
        <WorkflowStepper steps={workflowSteps} onStepClick={focusStep} />
      </section>

      {/* Cross-step employee finder — fuzzy local search + AI query
          parsing (§L.3). Overview mode only; focus mode already scopes
          the screen to one step. */}
      {!focusedStep && submissions.length > 0 && (
        <EmployeeFinder
          submissions={submissions}
          appraisals={appraisals}
          openAppeals={appeals}
          onJumpToStep={focusStep}
        />
      )}

      {focusedStep ? (
        // ── Wizard mode — single focused step + nav ─────────────
        <>
          <FocusNavBar
            current={focusedStep}
            onPrev={() => focusStep(focusedStep - 1)}
            onNext={() => focusStep(focusedStep + 1)}
            onBack={backToOverview}
          />
          {renderStepCard(focusedStep)}
          <FocusNavBar
            current={focusedStep}
            onPrev={() => focusStep(focusedStep - 1)}
            onNext={() => focusStep(focusedStep + 1)}
            onBack={backToOverview}
          />
        </>
      ) : (
        // ── Overview mode — progressive disclosure ──────────────
        // Show steps that are "active or imminent" by default; later steps
        // collapse into a slim "Later steps" preview. HR can still expand
        // any future step by clicking the stepper at top (focus mode).
        <ProgressiveStepStack
          activeStep={step}
          renderStepCard={renderStepCard}
          onFocusStep={focusStep}
          workflowSteps={workflowSteps}
        />
      )}
      {!focusedStep && <DangerZoneCard cycle={cycle} onAfter={refresh} />}
    </div>
  );
};

// ─── Wizard mode nav bar ──────────────────────────────────────────

const STEP_TITLES = [
  '', 'Submit EPR', '360 Review', 'Consolidation', 'Calibration',
  'Sign-off', 'Appeals', 'Outcome',
];

const FocusNavBar: React.FC<{
  current: number;
  onPrev: () => void;
  onNext: () => void;
  onBack: () => void;
}> = ({ current, onPrev, onNext, onBack }) => (
  <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">
    <Button variant="tertiary" icon="dashboard" onClick={onBack}>Overview</Button>
    <div className="text-sm text-stone-600">
      Step <b>{current}</b> of 7 — <b>{STEP_TITLES[current]}</b>
    </div>
    <div className="flex gap-2">
      <Button
        variant="secondary"
        icon="arrow_back"
        onClick={onPrev}
        disabled={current === 1}
      >Previous</Button>
      <Button
        variant="primary"
        icon="arrow_forward"
        iconPosition="trailing"
        onClick={onNext}
        disabled={current === 7}
      >Next</Button>
    </div>
  </section>
);

// ─── Danger Zone — reset cycle (dev / staging) ────────────────────

const DangerZoneCard: React.FC<{
  cycle: ReviewCycle;
  onAfter: () => void;
}> = ({ cycle, onAfter }) => {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  const canConfirm = typed.trim() === cycle.name && !busy;

  const handleReset = useCallback(async () => {
    setBusy(true);
    try {
      const res = await resetCycleData(cycle.id);
      toast.show(
        `Reset done. ${res.totalDeleted} docs deleted.`,
        { icon: 'delete' },
      );
      setExpanded(false);
      setTyped('');
      onAfter();
    } catch (e) {
      toast.show(`Reset gagal: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [cycle.id, cycle.name, toast, onAfter]);

  return (
    <section className="rounded-xl border border-red-200 bg-red-50 p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-rounded text-red-600" style={{ fontSize: 22 }} aria-hidden="true">
            warning
          </span>
          <h2 className="text-base font-semibold text-red-900">Danger Zone</h2>
        </div>
        <Button
          variant="tertiary"
          icon={expanded ? 'expand_less' : 'expand_more'}
          onClick={() => setExpanded(e => !e)}
        >
          {expanded ? 'Hide' : 'Show'}
        </Button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-red-900 leading-relaxed">
            <b>Reset cycle</b> will <b>permanently delete</b> every hr_v1
            document tied to this cycle:
          </p>
          <ul className="text-xs text-red-800 list-disc ml-5 space-y-0.5">
            <li>All EPR submissions</li>
            <li>All 360 submissions</li>
            <li>All magic-link tokens</li>
            <li>Calibration session + adjustments</li>
            <li>Appraisal forms (including signatures)</li>
            <li>Appeal records</li>
            <li>Outcome records</li>
          </ul>
          <p className="text-xs text-red-800">
            The ReviewCycle is reset to <code>status='draft'</code> /
            <code>currentStep=null</code>. OPTICS data, templates and users
            are not touched.
          </p>
          <p className="text-sm text-red-900 mt-3">
            To confirm, type the cycle name: <code className="font-bold">{cycle.name}</code>
          </p>
          <input
            type="text"
            value={typed}
            onChange={e => setTyped(e.target.value)}
            placeholder={cycle.name}
            className="w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => { setExpanded(false); setTyped(''); }}>
              Cancel
            </Button>
            <Button
              variant="submit"
              icon="delete_forever"
              loading={busy}
              disabled={!canConfirm}
              onClick={handleReset}
              className="!bg-red-700 hover:!bg-red-800"
            >
              Reset all cycle data
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Stats helper ─────────────────────────────────────────────────

function computeStats(
  subs: EPRSubmission[],
  apps: AppraisalForm[],
  openAppeals: AppealRecord[],
) {
  return {
    submissionsTotal:      subs.length,
    submissionsByEmployee: subs.filter(s => s.status !== 'draft').length,
    submissionsByManager:  subs.filter(s => s.status === 'manager_submitted' || s.status === 'locked').length,
    submissionsLocked:     subs.filter(s => s.status === 'locked').length,

    appraisalsTotal:       apps.length,
    appraisalsSent:        apps.filter(a => a.status !== 'draft').length,
    appraisalsEmpSigned:   apps.filter(a => a.status === 'employee_signed' || a.status === 'manager_signed' || a.status === 'closed').length,
    appraisalsMgrSigned:   apps.filter(a => a.status === 'manager_signed' || a.status === 'closed').length,
    appraisalsClosed:      apps.filter(a => a.status === 'closed').length,
    appraisalsWithOutcome: apps.filter(a => a.outcomeId).length,

    openAppeals: openAppeals.length,
  };
}

// ─── Step 1 — EPR launch ──────────────────────────────────────────

const Step1Card: React.FC<{
  cycle: ReviewCycle;
  submissions: EPRSubmission[];
  stats: ReturnType<typeof computeStats>;
  onAfter: () => void;
}> = ({ cycle, submissions, stats }) => {
  const [directory, setDirectory] = useState<DirectoryEmployee[]>([]);

  // Load directory eagerly when there are existing submissions — needed
  // for the pending list to show real names instead of raw ids.
  useEffect(() => {
    if (directory.length > 0 || submissions.length === 0) return;
    let alive = true;
    void fetchDirectory().then(r => { if (alive) setDirectory(r); }).catch(() => undefined);
    return () => { alive = false; };
  }, [directory.length, submissions.length]);

  const directoryById = React.useMemo(() => {
    const map = new Map<string, DirectoryEmployee>();
    directory.forEach(d => map.set(d.id, d));
    return map;
  }, [directory]);

  // Compute pending list — submissions that are not yet manager_submitted.
  const pendingItems: PendingListItem[] = React.useMemo(() => {
    return submissions
      .filter(s => s.status !== 'manager_submitted' && s.status !== 'locked')
      .map(s => {
        const emp = directoryById.get(s.employeeId);
        const sup = directoryById.get(s.supervisorId);
        const statusLabel =
          s.status === 'draft'              ? 'self not submitted'
          : s.status === 'employee_submitted' ? `waiting on ${sup?.name ?? 'manager'}`
          : s.status;
        return {
          id:       s.id,
          name:     emp?.name ?? s.employeeId,
          subtitle: emp?.email ?? undefined,
          status:   statusLabel,
          tone:     s.status === 'draft' ? 'warning' : 'info',
        } satisfies PendingListItem;
      });
  }, [submissions, directoryById]);

  const total = submissions.length;
  const doneCount = submissions.filter(
    s => s.status === 'manager_submitted' || s.status === 'locked',
  ).length;
  const pendingCount = total - doneCount;

  return (
    <StepCard
      step={1}
      title="EPR Submission"
      status={
        total === 0 ? 'not started'
        : pendingCount === 0 ? 'all done'
        : `${doneCount}/${total} · ${pendingCount} left`
      }
      tone={
        total === 0 ? 'neutral'
        : pendingCount === 0 ? 'success'
        : 'info'
      }
      summary={
        <ul className="text-sm text-stone-700 space-y-1">
          <li>Total submission: <b>{stats.submissionsTotal}</b></li>
          <li>Submitted by employee: <b>{stats.submissionsByEmployee}/{stats.submissionsTotal}</b></li>
          <li>Submitted by manager: <b>{stats.submissionsByManager}/{stats.submissionsTotal}</b></li>
        </ul>
      }
      action={
        <Link to={routes.hrV1LaunchEpr(cycle.id)}>
          <Button variant="primary" icon="play_arrow">
            {submissions.length === 0 ? 'Launch Step 1' : 'Re-launch / Add employees'}
          </Button>
        </Link>
      }
    >
      {/* Per-employee pending list (Pattern C-Lite). Done employees are
          not rendered here — uncluttered by design. */}
      <PendingList items={pendingItems} noun="left to submit" />
    </StepCard>
  );
};

// ─── Step 2 — 360 panel launch ────────────────────────────────────

const Step2Card: React.FC<{
  cycle: ReviewCycle;
  submissions: EPRSubmission[];
  onAfter: () => void;
}> = ({ cycle, submissions, onAfter }) => {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reviewees, setReviewees] = useState<LaunchStep2Input['reviewees']>([]);
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState<DistributionLink[]>([]);

  const handleLaunch = useCallback(async () => {
    setBusy(true);
    try {
      if (reviewees.length === 0) throw new Error("No reviewees selected.");
      const res = await launchStep2({
        cycleId: cycle.id,
        reviewees,
      });
      setLinks(res.panelEntries.map(p => ({
        recipientEmail: p.reviewerEmail,
        action:         '360 feedback',
        context:        `for ${p.revieweeName} (${p.relationship})`,
        url:            p.url,
      })));
      toast.show(`Step 2 launched — ${res.reviewerTokens.length} 360 tokens`, { icon: 'check' });
      onAfter();
    } catch (e) {
      toast.show(`Launch failed: ${(e as Error).message}`, { icon: 'error' });
    } finally {
      setBusy(false);
    }
  }, [cycle, reviewees, toast, onAfter]);

  return (
    <StepCard
      step={2}
      title="360 Review"
      status={cycle.currentStep && cycle.currentStep >= 2 ? 'active' : 'not started'}
      tone={cycle.currentStep && cycle.currentStep >= 2 ? 'info' : 'neutral'}
      summary={
        <p className="text-sm text-stone-700">
          Once Step 1 is launched, a 360 panel is issued for each reviewee.
          Visibility per relationship is set on the template (default:
          manager attributed, peer/subordinate anonymous).
        </p>
      }
      action={
        <Button
          variant="primary"
          icon="play_arrow"
          onClick={() => setOpen(true)}
          disabled={submissions.length === 0}
        >
          Compose & Launch panel
        </Button>
      }
    >
      {open && (
        <div className="mt-4 space-y-4 border-t border-stone-200 pt-4">
          <PanelBuilder 
            submissions={submissions}
            value={reviewees}
            onChange={setReviewees}
          />
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="submit" loading={busy} onClick={handleLaunch} disabled={reviewees.length === 0}>Launch panel</Button>
          </div>
          {links.length > 0 && (
            <DistributionPackage
              title="360 panel links ready"
              cycleName={cycle.name}
              links={links}
            />
          )}
        </div>
      )}
    </StepCard>
  );
};

// ─── Step 3 — Consolidation ────────────────────────────────────────

const Step3Card: React.FC<{
  cycle: ReviewCycle;
  submissions: EPRSubmission[];
  appraisals: AppraisalForm[];
  onAfter: () => void;
}> = ({ cycle, submissions, appraisals, onAfter }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const ready = submissions.filter(
    s => s.status === 'manager_submitted' || s.status === 'locked',
  ).length;

  const handleConsolidate = useCallback(async () => {
    setBusy(true);
    try {
      const res = await consolidateCycle(cycle.id);
      toast.show(
        `Consolidated ${res.appraisals.length} appraisal${res.appraisals.length !== 1 ? 's' : ''}.` +
        (res.skipped.length ? ` ${res.skipped.length} skipped.` : ''),
        { icon: 'check' },
      );
      onAfter();
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { icon: 'error' });
    } finally {
      setBusy(false);
    }
  }, [cycle.id, toast, onAfter]);

  return (
    <StepCard
      step={3}
      title="Consolidation"
      status={appraisals.length > 0 ? 'done' : 'pending'}
      tone={appraisals.length > 0 ? 'success' : 'neutral'}
      summary={
        <ul className="text-sm text-stone-700 space-y-1">
          <li>Submissions ready (manager_submitted+): <b>{ready}/{submissions.length}</b></li>
          <li>Draft appraisals created: <b>{appraisals.length}</b></li>
        </ul>
      }
      action={
        <Button
          variant="primary"
          icon="merge"
          onClick={handleConsolidate}
          loading={busy}
          disabled={ready === 0}
        >
          {appraisals.length > 0 ? 'Re-consolidate' : 'Consolidate now'}
        </Button>
      }
    />
  );
};

// ─── Step 4 — Calibration ─────────────────────────────────────────

const Step4Card: React.FC<{
  cycle: ReviewCycle;
  session: CalibrationSession | null;
  submissions: EPRSubmission[];
  userId: string;
  onAfter: () => void;
}> = ({ cycle, session, submissions, userId, onAfter }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);
  const [justifications, setJustifications] = useState<Partial<Record<CalibrationBand, string>>>({});

  // Bias-reduction analytics — recomputed each render from current submissions
  const extremes  = findExtremeScores(submissions);
  const mgrStats  = detectManagerBias(submissions);
  const biasMgrs  = mgrStats.filter(m => m.bias !== 'neutral');

  const startSession = useCallback(async () => {
    setBusy(true);
    try {
      await createSession({
        cycleId: cycle.id,
        attendees: { managerIds: [], hrIds: [userId], mdId: undefined },
      });
      toast.show('Calibration session created.', { icon: 'check' });
      onAfter();
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [cycle.id, userId, toast, onAfter]);

  const compute = useCallback(async () => {
    setBusy(true);
    try {
      await computeAndStoreDistribution({ cycleId: cycle.id, justifications });
      toast.show('Distribution computed.', { icon: 'check' });
      onAfter();
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [cycle.id, justifications, toast, onAfter]);

  const askLock = useCallback(() => setLockConfirmOpen(true), []);
  const doLock = useCallback(async () => {
    setLockConfirmOpen(false);
    setBusy(true);
    try {
      await lockSession({ cycleId: cycle.id, lockedBy: userId });
      toast.show('Calibration locked.', { icon: 'check' });
      onAfter();
    } catch (e) {
      toast.show(`Lock failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [cycle.id, userId, toast, onAfter]);

  return (
    <StepCard
      step={4}
      title="Calibration"
      status={session?.status === 'locked' ? 'locked'
        : session?.status === 'in_session' ? 'in session'
        : session?.status === 'preparing' ? 'preparing'
        : 'not started'}
      tone={session?.status === 'locked' ? 'success' : session ? 'info' : 'neutral'}
      summary={session ? (
        <div className="text-sm text-stone-700 space-y-2">
          <div>Total employees: <b>{session.totalEmployees}</b></div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-stone-500 border-b">
                <th className="text-left py-1">Band</th>
                <th className="text-right">Count</th>
                <th className="text-right">%</th>
                <th className="text-right">Target</th>
                <th className="text-right">Allowed</th>
                <th className="text-left pl-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {CALIBRATION_BANDS.map(b => {
                const row = session.distribution[b];
                const t = BELL_CURVE_TARGETS[b];
                return (
                  <tr key={b} className="border-b border-stone-100">
                    <td className="py-1">{CALIBRATION_BAND_LABELS[b]}</td>
                    <td className="text-right">{row.count}</td>
                    <td className="text-right">{row.pct.toFixed(1)}%</td>
                    <td className="text-right text-stone-500">{t.target.min}-{t.target.max}%</td>
                    <td className="text-right text-stone-500">{t.allowed.min}-{t.allowed.max}%</td>
                    <td className="pl-2">
                      <span className={[
                        'px-1.5 py-0.5 rounded text-xs',
                        row.status === 'within' ? 'bg-emerald-100 text-emerald-800' :
                        row.status === 'review' ? 'bg-amber-100 text-amber-900' :
                                                   'bg-red-100 text-red-800',
                      ].join(' ')}>{row.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {CALIBRATION_BANDS.some(b => session.distribution[b].status === 'review') && (
            <div className="space-y-2 pt-2 border-t border-stone-200">
              <div className="text-xs font-medium text-stone-700">Justification for bands in 'review' status:</div>
              {CALIBRATION_BANDS.filter(b => session.distribution[b].status === 'review').map(b => (
                <textarea
                  key={b}
                  placeholder={`Justification for ${CALIBRATION_BAND_LABELS[b]}`}
                  defaultValue={session.distribution[b].justification ?? ''}
                  onChange={e => setJustifications(j => ({ ...j, [b]: e.target.value }))}
                  rows={2}
                  className="w-full rounded-lg border border-stone-300 px-2 py-1 text-xs"
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-stone-600">No session yet. Start one to compute the distribution.</p>
      )}
      action={
        <div className="flex flex-wrap gap-2 justify-end">
          {!session && (
            <Button variant="primary" icon="play_arrow" loading={busy} onClick={startSession}>
              Start session
            </Button>
          )}
          {session && session.status !== 'locked' && (
            <>
              <Button variant="secondary" icon="calculate" loading={busy} onClick={compute}>
                Compute distribution
              </Button>
              <Button
                variant="submit"
                icon="lock"
                loading={busy}
                onClick={askLock}
                disabled={CALIBRATION_BANDS.some(b => session.distribution[b].status === 'out')}
              >
                Lock & advance to Step 5
              </Button>
            </>
          )}
        </div>
      }
    >
      {/* Bias-reduction panels — framework Calibration Session checklist */}
      {(extremes.length > 0 || biasMgrs.length > 0) && (
        <div className="mt-4 pt-4 border-t border-stone-200 space-y-4">
          {extremes.length > 0 && (
            <ExtremeScoresPanel extremes={extremes} />
          )}
          {biasMgrs.length > 0 && (
            <ManagerBiasPanel stats={mgrStats} biased={biasMgrs} />
          )}
        </div>
      )}

      <ConfirmDialog
        open={lockConfirmOpen}
        title="Lock calibration — IRREVERSIBLE"
        tone="danger"
        body="Locking the calibration session writes every calibrated score back into the EPR submissions, rebuilds the appraisal forms, and advances the cycle to Step 5. After this you can only change scores via a formal Appeal."
        actions={[
          { label: 'All EPRSubmissions locked at their current scores',     detail: 'Item-level edits no longer permitted' },
          { label: 'AppraisalForms rebuilt with calibrated totals',         detail: 'eprSummary refreshed' },
          { label: `Cycle currentStep advances 4 → 5`,                      detail: 'Sign-off phase begins' },
          { label: 'Adjustments audit row preserved per §E.6',              detail: 'Immutable; nothing is deleted' },
        ]}
        confirmLabel="Lock & continue"
        requireTypedConfirmation={cycle.name}
        loading={busy}
        onCancel={() => setLockConfirmOpen(false)}
        onConfirm={doLock}
      />
    </StepCard>
  );
};

// ─── Bias-reduction sub-panels (framework Calibration Session) ───

const ExtremeScoresPanel: React.FC<{ extremes: ExtremeScoreRow[] }> = ({ extremes }) => {
  const [open, setOpen] = useState(false);
  const noEvidence = extremes.filter(e => !e.hasEvidence).length;
  const fives = extremes.filter(e => e.managerScore === 5).length;
  const ones  = extremes.filter(e => e.managerScore <= 2).length;

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="material-symbols-rounded text-amber-700" style={{ fontSize: 20 }}>flag</span>
          <h4 className="text-sm font-semibold text-amber-900">
            5s &amp; 1s review — verify exceptional / unsatisfactory ratings
          </h4>
        </div>
        <Button variant="tertiary" icon={open ? 'expand_less' : 'expand_more'} onClick={() => setOpen(o => !o)}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </div>
      <div className="text-xs text-amber-900 mt-1">
        <b>{fives}</b> score-5 · <b>{ones}</b> score-1/2 ·
        <b className={noEvidence > 0 ? 'text-red-700' : ''}> {noEvidence}</b> missing evidence
      </div>
      {open && (
        <div className="mt-3 max-h-80 overflow-y-auto space-y-1 text-xs">
          {extremes.map(e => (
            <div
              key={`${e.submissionId}-${e.itemId}`}
              className={[
                'rounded border bg-white px-3 py-2 flex flex-wrap items-baseline gap-x-3 gap-y-1',
                e.hasEvidence ? 'border-stone-200' : 'border-red-300',
              ].join(' ')}
            >
              <span className={[
                'px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0',
                e.managerScore === 5 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800',
              ].join(' ')}>
                M:{e.managerScore}
                {typeof e.employeeSelfScore === 'number' && ` · S:${e.employeeSelfScore}`}
                {typeof e.gap === 'number' && ` · Δ${e.gap}`}
              </span>
              <span className="text-stone-500 text-[10px] uppercase">{e.component}</span>
              <span className="font-medium text-stone-900 truncate">{e.itemName}</span>
              <span className="text-stone-500">· {e.employeeId} (mgr {e.supervisorId})</span>
              {!e.hasEvidence && (
                <span className="text-red-700 font-semibold">⚠ No evidence</span>
              )}
              {e.notesEvidence && (
                <div className="w-full text-stone-700 italic mt-0.5">"{e.notesEvidence}"</div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const ManagerBiasPanel: React.FC<{
  stats: ManagerRatingStat[];
  biased: ManagerRatingStat[];
}> = ({ stats, biased }) => {
  const orgAvg = stats[0]?.orgAvg ?? 0;
  return (
    <section className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="material-symbols-rounded text-indigo-700" style={{ fontSize: 20 }}>balance</span>
        <h4 className="text-sm font-semibold text-indigo-900">
          Lenient / strict managers — bias signal vs org average
        </h4>
      </div>
      <div className="text-xs text-indigo-900 mb-3">
        Org-wide manager-score average: <b>{orgAvg.toFixed(2)}</b> ·
        Threshold ±0.50 · <b>{biased.length}</b> flagged
      </div>
      <div className="space-y-1">
        {biased.map(m => (
          <div
            key={m.supervisorId}
            className={[
              'rounded border bg-white px-3 py-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs',
              m.bias === 'lenient' ? 'border-amber-300' :
              m.bias === 'strict'  ? 'border-red-300' :
                                     'border-stone-200',
            ].join(' ')}
          >
            <span className={[
              'px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 capitalize',
              m.bias === 'lenient' ? 'bg-amber-100 text-amber-900' :
              m.bias === 'strict'  ? 'bg-red-100 text-red-800' :
                                     'bg-stone-100 text-stone-700',
            ].join(' ')}>
              {m.bias}
            </span>
            <code className="font-medium text-stone-900">{m.supervisorId}</code>
            <span className="text-stone-700">
              avg <b>{m.avgGiven.toFixed(2)}</b>
              <span className={m.deviation > 0 ? 'text-amber-700' : 'text-red-700'}>
                {' '}({m.deviation > 0 ? '+' : ''}{m.deviation.toFixed(2)})
              </span>
            </span>
            <span className="text-stone-500">
              · {m.reviewees} reviewee{m.reviewees !== 1 ? 's' : ''} · {m.itemsScored} items
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── Step 5 — Appraisal editing + send ────────────────────────────

const Step5Card: React.FC<{
  cycle: ReviewCycle;
  appraisals: AppraisalForm[];
  onAfter: () => void;
}> = ({ cycle, appraisals, onAfter }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [directory, setDirectory] = useState<DirectoryEmployee[]>([]);

  // Eager-load directory whenever appraisals exist so pending names render.
  useEffect(() => {
    if (directory.length > 0 || appraisals.length === 0) return;
    let alive = true;
    void fetchDirectory().then(r => { if (alive) setDirectory(r); }).catch(() => undefined);
    return () => { alive = false; };
  }, [directory.length, appraisals.length]);

  const directoryById = React.useMemo(() => {
    const map = new Map<string, DirectoryEmployee>();
    directory.forEach(d => map.set(d.id, d));
    return map;
  }, [directory]);

  const sent     = appraisals.filter(a => a.status !== 'draft').length;
  const empSign  = appraisals.filter(a => a.status === 'employee_signed' || a.status === 'manager_signed' || a.status === 'closed').length;
  const mgrSign  = appraisals.filter(a => a.status === 'manager_signed' || a.status === 'closed').length;

  // Pending = anyone NOT yet manager-signed or closed. HR's "still chasing" list.
  const pendingItems: PendingListItem[] = React.useMemo(() => {
    return appraisals
      .filter(a => a.status !== 'manager_signed' && a.status !== 'closed')
      .map(a => {
        const emp = directoryById.get(a.employeeId);
        const statusLabel =
          a.status === 'draft'             ? 'draft — not sent'
          : a.status === 'sent_to_employee' ? 'awaiting employee signature'
          : a.status === 'employee_signed'  ? 'awaiting manager signature'
          : a.status;
        return {
          id:          a.id,
          name:        emp?.name ?? a.employeeId,
          subtitle:    emp?.email ?? undefined,
          status:      statusLabel,
          tone:        a.status === 'draft' ? 'warning' : 'info',
          actionLabel: a.status === 'draft' ? 'Edit & Send' : 'Open',
          actionIcon:  a.status === 'draft' ? 'send' : 'open_in_new',
          onAction:    () => setOpenId(a.id),
        } satisfies PendingListItem;
      });
  }, [appraisals, directoryById]);

  const total = appraisals.length;
  const pendingCount = total - mgrSign;

  return (
    <StepCard
      step={5}
      title="Appraisal & sign-off"
      status={
        total === 0 ? 'not ready'
        : pendingCount === 0 ? 'all signed'
        : `${mgrSign}/${total} · ${pendingCount} left`
      }
      tone={
        total === 0 ? 'neutral'
        : pendingCount === 0 ? 'success'
        : 'info'
      }
      summary={
        <ul className="text-sm text-stone-700 space-y-1">
          <li>Total appraisal: <b>{total}</b></li>
          <li>Sent to employee: <b>{sent}/{total}</b></li>
          <li>Employee signed: <b>{empSign}/{total}</b></li>
          <li>Manager signed: <b>{mgrSign}/{total}</b></li>
        </ul>
      }
    >
      <PendingList
        items={pendingItems}
        noun="left to sign"
        emptyMessage={total === 0 ? undefined : 'All appraisals signed by both parties.'}
      />
      {openId && (
        <AppraisalEditorModal
          cycleId={cycle.id}
          appraisal={appraisals.find(a => a.id === openId)!}
          onClose={() => { setOpenId(null); onAfter(); }}
        />
      )}
    </StepCard>
  );
};

const AppraisalEditorModal: React.FC<{
  cycleId: string;
  appraisal: AppraisalForm;
  onClose: () => void;
}> = ({ cycleId, appraisal, onClose }) => {
  const toast = useToast();
  const [notes, setNotes] = useState(appraisal.managerCombinedNotes);
  const [planJson, setPlanJson] = useState(JSON.stringify(appraisal.developmentPlan, null, 2));
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [managerEmail,  setManagerEmail]  = useState('');
  const [busy, setBusy] = useState(false);
  const [urls, setUrls] = useState<{ employee?: string; manager?: string; view?: string }>({});

  const saveDraft = useCallback(async () => {
    setBusy(true);
    try {
      await updateManagerNotes(appraisal.id, notes);
      const plan = JSON.parse(planJson);
      await setDevelopmentPlan(appraisal.id, plan);
      toast.show('Draft saved.', { icon: 'check' });
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [appraisal.id, notes, planJson, toast]);

  const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

  const send = useCallback(async () => {
    const empOk = isValidEmail(employeeEmail);
    const mgrOk = isValidEmail(managerEmail);
    if (!empOk || !mgrOk) {
      toast.show(
        !empOk && !mgrOk ? 'Both employee and manager emails are required and must be valid.'
        : !empOk         ? 'Employee email is required and must be valid.'
        :                  'Manager email is required and must be valid.',
        { icon: 'warning' },
      );
      return;
    }
    setBusy(true);
    try {
      await updateManagerNotes(appraisal.id, notes);
      let plan;
      try {
        plan = JSON.parse(planJson);
        if (!Array.isArray(plan)) throw new Error('Development plan must be a JSON array');
      } catch (parseErr) {
        toast.show(`Development plan: ${(parseErr as Error).message}`, { icon: 'error' });
        setBusy(false);
        return;
      }
      await setDevelopmentPlan(appraisal.id, plan);
      const res = await sendToEmployee({
        appraisalId: appraisal.id,
        cycleId,
        employee: { id: appraisal.employeeId,  email: employeeEmail.trim() },
        manager:  { id: appraisal.supervisorId, email: managerEmail.trim() },
      });
      setUrls({ employee: res.signEmployee, manager: res.signManager, view: res.viewEmployee });
      toast.show('Appraisal sent. Copy URLs for distribution.', { icon: 'check' });
    } catch (e) {
      toast.show(`Send failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [appraisal, cycleId, notes, planJson, employeeEmail, managerEmail, toast]);

  return (
    <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit Appraisal — {appraisal.employeeId}</h3>
          <Button variant="tertiary" icon="close" onClick={onClose}>Close</Button>
        </div>
        <AppraisalSummaryView appraisal={appraisal} />
        <div className="mt-6 space-y-3">
          <label className="block text-sm font-medium">Manager notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
          />
          <label className="block text-sm font-medium">Development plan (JSON array)</label>
          <textarea
            value={planJson}
            onChange={e => setPlanJson(e.target.value)}
            rows={8}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-xs font-mono"
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="email" placeholder="Employee email" value={employeeEmail}
              onChange={e => setEmployeeEmail(e.target.value)}
              className="rounded-lg border border-stone-300 px-3 py-2 text-sm"
            />
            <input
              type="email" placeholder="Manager email" value={managerEmail}
              onChange={e => setManagerEmail(e.target.value)}
              className="rounded-lg border border-stone-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" loading={busy} onClick={saveDraft}>Save draft</Button>
            <Button variant="submit" loading={busy} onClick={send}>Send to employee</Button>
          </div>
          {(urls.employee || urls.manager || urls.view) && (
            <DistributionPackage
              title="Appraisal links ready"
              cycleName={cycleId}
              links={[
                urls.employee && {
                  recipientEmail: employeeEmail,
                  action:         'Sign appraisal (employee)',
                  url:            urls.employee,
                },
                urls.manager && {
                  recipientEmail: managerEmail,
                  action:         'Sign appraisal (manager)',
                  context:        `for ${appraisal.employeeId}`,
                  url:            urls.manager,
                },
                urls.view && {
                  recipientEmail: employeeEmail,
                  action:         'View result (employee, reusable)',
                  url:            urls.view,
                },
              ].filter(Boolean) as DistributionLink[]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Step 6 — Appeals + auto-advance ──────────────────────────────

const Step6Card: React.FC<{
  cycle: ReviewCycle;
  appeals: AppealRecord[];
  userId: string;
  onAfter: () => void;
}> = ({ cycle, appeals, userId, onAfter }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const tryAdvance = useCallback(async () => {
    setBusy(true);
    try {
      const res = await advanceCyclesIfDue();
      const me = res.find(r => r.cycleId === cycle.id);
      toast.show(
        me ? `${me.reason} — ${me.notes ?? 'advanced'}` : 'Tidak ada perubahan.',
        { icon: 'info' },
      );
      onAfter();
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [cycle.id, toast, onAfter]);

  return (
    <StepCard
      step={6}
      title="Appeal (optional, 2-day window)"
      status={appeals.length === 0 ? 'no appeals' : `${appeals.length} open appeal${appeals.length > 1 ? 's' : ''}`}
      tone={appeals.length === 0 ? 'success' : 'warning'}
      summary={
        appeals.length === 0
          ? <p className="text-sm text-stone-600">Auto-advances to Step 7 if no appeal is filed within 2 days after manager sign-off.</p>
          : (
            <div className="text-sm space-y-2">
              {appeals.map(a => (
                <AppealRow key={a.id} appeal={a} userId={userId} onAfter={onAfter} />
              ))}
            </div>
          )
      }
      action={
        <Button variant="secondary" icon="schedule" loading={busy} onClick={tryAdvance}>
          Check auto-advance
        </Button>
      }
    />
  );
};

const AppealRow: React.FC<{
  appeal: AppealRecord;
  userId: string;
  onAfter: () => void;
}> = ({ appeal, userId, onAfter }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');

  const decide = useCallback(async (decision: 'upheld_original' | 'partially_adjusted' | 'fully_revised') => {
    setBusy(true);
    try {
      await decideAppeal({
        appealId: appeal.id, decision, decisionBy: userId, decisionNotes: notes,
      });
      toast.show('Appeal decided.', { icon: 'check' });
      onAfter();
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [appeal.id, userId, notes, toast, onAfter]);

  return (
    <div className="border border-stone-200 rounded-xl p-3 bg-stone-50">
      <div className="text-xs text-stone-500">Employee: {appeal.employeeId} · Status: {appeal.status}</div>
      <div className="text-sm whitespace-pre-wrap mt-1">{appeal.filedReason}</div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Decision notes (optional)"
        rows={2}
        className="w-full mt-2 rounded border border-stone-300 px-2 py-1 text-xs"
      />
      <div className="flex flex-wrap gap-2 justify-end mt-2">
        <Button variant="secondary" loading={busy} onClick={() => decide('upheld_original')}>Uphold</Button>
        <Button variant="secondary" loading={busy} onClick={() => decide('partially_adjusted')}>Partially adjust</Button>
        <Button variant="secondary" loading={busy} onClick={() => decide('fully_revised')}>Fully revise</Button>
      </div>
    </div>
  );
};

// ─── Step 7 — Outcome ─────────────────────────────────────────────

const Step7Card: React.FC<{
  cycle: ReviewCycle;
  appraisals: AppraisalForm[];
  userId: string;
  onAfter: () => void;
}> = ({ cycle, appraisals, userId, onAfter }) => {
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const ready = appraisals.filter(a => a.status === 'manager_signed' || a.status === 'closed');
  const withOutcome = appraisals.filter(a => a.outcomeId).length;

  const askClose = useCallback(() => setCloseConfirmOpen(true), []);
  const doCloseCycle = useCallback(async () => {
    setCloseConfirmOpen(false);
    setBusy(true);
    try {
      await closeCycle(cycle.id);
      toast.show('Cycle closed.', { icon: 'check' });
      onAfter();
    } catch (e) {
      toast.show(`Close failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [cycle.id, toast, onAfter]);

  return (
    <StepCard
      step={7}
      title="Outcome"
      status={cycle.status === 'closed' ? 'cycle closed' : `${withOutcome}/${appraisals.length} processed`}
      tone={cycle.status === 'closed' ? 'success' : withOutcome > 0 ? 'info' : 'neutral'}
      summary={
        <div className="text-sm space-y-2">
          {ready.length === 0
            ? <p className="text-stone-600">No appraisals ready for outcome yet.</p>
            : ready.map(a => (
                <div key={a.id} className="flex items-center justify-between gap-2 border-b border-stone-100 py-1">
                  <div className="text-xs">
                    <code>{a.employeeId}</code>
                    {a.outcomeId && <span className="ml-2 text-emerald-700">✓ processed</span>}
                  </div>
                  {!a.outcomeId && (
                    <Button variant="secondary" onClick={() => setOpenId(a.id)}>Record outcome</Button>
                  )}
                </div>
              ))}
        </div>
      }
      action={
        <Button
          variant="submit"
          icon="check_circle"
          loading={busy}
          disabled={appraisals.length === 0 || withOutcome < appraisals.length}
          onClick={askClose}
        >
          Close cycle
        </Button>
      }
    >
      <ConfirmDialog
        open={closeConfirmOpen}
        title="Close cycle — final state"
        tone="danger"
        body={`This marks the cycle as closed. All ${appraisals.length} appraisal${appraisals.length !== 1 ? 's' : ''} have outcomes recorded. After close, the cycle is archived.`}
        actions={[
          { label: `ReviewCycle status: active → closed`,                      detail: 'Cycle removed from active workflows' },
          { label: `currentStep stamped at 7`,                                  detail: 'Outcome phase complete' },
          { label: `All ${withOutcome}/${appraisals.length} outcome records preserved` },
        ]}
        confirmLabel="Close cycle"
        requireTypedConfirmation={cycle.name}
        loading={busy}
        onCancel={() => setCloseConfirmOpen(false)}
        onConfirm={doCloseCycle}
      />

      {openId && (
        <OutcomeModal
          cycleId={cycle.id}
          appraisal={appraisals.find(a => a.id === openId)!}
          userId={userId}
          onClose={() => { setOpenId(null); onAfter(); }}
        />
      )}
    </StepCard>
  );
};

const OutcomeModal: React.FC<{
  cycleId: string;
  appraisal: AppraisalForm;
  userId: string;
  onClose: () => void;
}> = ({ cycleId, appraisal, userId, onClose }) => {
  const toast = useToast();
  const [type, setType] = useState<OutcomeType>('no_change');
  const [incrementAmount, setIncrementAmount] = useState<string>('');
  const [promoTitle, setPromoTitle] = useState('');
  const [pipRef, setPipRef] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const save = useCallback(async () => {
    setBusy(true);
    try {
      await recordOutcome({
        cycleId,
        employeeId: appraisal.employeeId,
        appraisalId: appraisal.id,
        outcomeType: type,
        processedBy: userId,
        incrementAmount: incrementAmount ? Number(incrementAmount) : undefined,
        promotionToTitle: promoTitle || undefined,
        pipFormRef: pipRef || undefined,
        notes,
      });
      toast.show('Outcome recorded.', { icon: 'check' });
      onClose();
    } catch (e) {
      toast.show(`Failed: ${(e as Error).message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [cycleId, appraisal, type, incrementAmount, promoTitle, pipRef, notes, userId, toast, onClose]);

  return (
    <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-3">Record outcome — {appraisal.employeeId}</h3>
        <div className="space-y-3 text-sm">
          <select
            value={type}
            onChange={e => setType(e.target.value as OutcomeType)}
            className="w-full rounded-lg border border-stone-300 px-3 py-2"
          >
            <option value="no_change">No change</option>
            <option value="increment">Increment</option>
            <option value="promotion">Promotion</option>
            <option value="increment_and_promotion">Increment + Promotion</option>
            <option value="pip">PIP</option>
          </select>
          {(type === 'increment' || type === 'increment_and_promotion') && (
            <input
              type="number" placeholder="Increment amount"
              value={incrementAmount} onChange={e => setIncrementAmount(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2"
            />
          )}
          {(type === 'promotion' || type === 'increment_and_promotion') && (
            <input
              type="text" placeholder="New title"
              value={promoTitle} onChange={e => setPromoTitle(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2"
            />
          )}
          {type === 'pip' && (
            <input
              type="text" placeholder="PIP form ref"
              value={pipRef} onChange={e => setPipRef(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2"
            />
          )}
          <textarea
            placeholder="Notes" value={notes} onChange={e => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-stone-300 px-3 py-2"
          />
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="submit" loading={busy} onClick={save}>Save outcome</Button>
        </div>
      </div>
    </div>
  );
};

// ─── Stale-id recovery card ──────────────────────────────────────
//
// When HrV1Console can't resolve a cycle (deleted, OPTICS-only, bad
// URL, stale bookmark), this card replaces the dead-end "Back to
// Cycles" button with an inline pick-list of available hr_v1 cycles.
// User-facing fix for the bug class "stale URL → dead-end".

const CycleNotFoundRecovery: React.FC<{ missingId?: string }> = ({ missingId }) => {
  const [options, setOptions] = useState<Array<{ id: string; name: string; period?: string; year?: number }>>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const cycles = await listHrV1Cycles();
        if (!alive) return;
        setOptions(cycles
          .slice(0, 6)
          .map(c => ({ id: c.id, name: c.name, period: c.period, year: c.year })),
        );
      } catch (e) {
        if (alive) setLoadError((e as Error).message);
      } finally {
        if (alive) setLoadingList(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-stone-50">
      <div className="max-w-lg w-full border rounded-2xl p-8 bg-red-50 border-red-200">
        <div className="text-center mb-4">
          <span className="material-symbols-rounded text-red-600" style={{ fontSize: 40 }} aria-hidden="true">
            error
          </span>
          <h2 className="text-xl font-semibold tracking-tight mt-3 mb-1 text-red-900">
            Cycle not found
          </h2>
          <p className="text-sm text-red-900">
            {missingId
              ? <>The cycle id <code className="text-xs bg-white px-1 rounded">{missingId}</code> doesn't exist, was removed, or isn't an hr_v1 cycle.</>
              : <>No cycle was specified in the URL.</>}
          </p>
        </div>

        {loadingList && (
          <div className="text-center text-sm text-stone-600">Loading available cycles…</div>
        )}

        {!loadingList && loadError && (
          <div className="text-sm text-red-800 bg-white border border-red-200 rounded-lg p-3">
            Could not load cycle list: {loadError}
          </div>
        )}

        {!loadingList && !loadError && options.length === 0 && (
          <div className="rounded-lg bg-white border border-stone-200 p-3 text-sm text-stone-700">
            No hr_v1 cycles exist yet. Create one from the Cycles page.
          </div>
        )}

        {!loadingList && !loadError && options.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-red-900 font-semibold mb-2">
              Pick an existing cycle
            </div>
            <ul className="space-y-1.5">
              {options.map(c => (
                <li key={c.id}>
                  <Link
                    to={routes.hrV1Cycle(c.id)}
                    className="flex items-center justify-between gap-3 rounded-lg bg-white border border-stone-200 hover:border-stone-400 px-3 py-2 text-sm text-stone-900"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      {(c.period || c.year) && (
                        <div className="text-xs text-stone-500">
                          {c.period} {c.year}
                        </div>
                      )}
                    </div>
                    <span className="material-symbols-rounded text-stone-400 flex-shrink-0" style={{ fontSize: 18 }}>
                      chevron_right
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex justify-center">
          <Link to={routes.cyclesList()}>
            <Button variant="secondary" icon="list">See all cycles</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// NOTE: WizardSection and TemplatePickerCompact previously lived here
// for the inline Step 1 launch form. That form moved out to
// HrV1LaunchEpr.tsx (full-page <LaunchWizard>). The helpers were
// deleted from this file in the same migration to avoid dead code;
// re-add them under components/epr/ if a future inline launcher needs
// the numbered-section pattern.

// ─── Progressive disclosure of step cards ─────────────────────────
//
// Default: shows the current step + the next one only. All other steps
// collapse into a single "Later steps" card with thin preview tiles.
// HR can expand the rest via the "Show all steps" toggle, or jump into
// any single step via the stepper at the top (focus mode).
//
// Reduces visual weight by ~70% at "not started" (only Step 1 visible)
// and keeps the workflow legible at any cycle state.

const ProgressiveStepStack: React.FC<{
  activeStep: number;                           // cycle.currentStep ?? 0
  renderStepCard: (n: number) => React.ReactNode;
  onFocusStep: (n: number) => void;
  workflowSteps: ReturnType<typeof deriveSteps>;
}> = ({ activeStep, renderStepCard, onFocusStep, workflowSteps }) => {
  const [showAll, setShowAll] = useState(false);

  // What counts as "visible by default":
  //   - The active step (or step 1 if cycle hasn't started)
  //   - The next step (so HR can preview what's coming)
  // Everything else collapses into the "Later steps" card.
  const currentEffective = Math.max(activeStep, 1);
  const visibleSet = new Set<number>([currentEffective, currentEffective + 1]);

  const allNums = [1, 2, 3, 4, 5, 6, 7];
  const visible = showAll ? allNums : allNums.filter(n => visibleSet.has(n));
  const hidden  = showAll ? []      : allNums.filter(n => !visibleSet.has(n));

  return (
    <>
      {visible.map(n => (
        <div key={n}>{renderStepCard(n)}</div>
      ))}

      {hidden.length > 0 && (
        <section className="rounded-xl border border-stone-200 bg-stone-50 p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-xs uppercase tracking-wide text-stone-500">
              Later steps · {hidden.length} hidden
            </div>
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-xs text-stone-600 hover:text-stone-900 underline"
            >
              Show all steps
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {hidden.map(n => {
              const ws = workflowSteps[n - 1];
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => onFocusStep(n)}
                  className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2 text-left hover:border-stone-400 transition"
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-stone-200 text-stone-700 font-semibold text-xs flex-shrink-0">
                    {n}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-stone-900 truncate">
                      {ws?.label ?? `Step ${n}`}
                    </div>
                    <div className="text-xs text-stone-500">
                      {ws?.status ?? 'pending'}
                    </div>
                  </div>
                  <span className="material-symbols-rounded text-stone-400" style={{ fontSize: 18 }}>
                    chevron_right
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {showAll && (
        <div className="text-right">
          <button
            type="button"
            onClick={() => setShowAll(false)}
            className="text-xs text-stone-600 hover:text-stone-900 underline"
          >
            Collapse later steps
          </button>
        </div>
      )}
    </>
  );
};

// ─── Shared step card ─────────────────────────────────────────────

const StepCard: React.FC<{
  step: number;
  title: string;
  status: string;
  tone: 'success' | 'info' | 'warning' | 'neutral';
  summary?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ step, title, status, tone, summary, action, children }) => (
  <section
    id={`step-card-${step}`}
    className="rounded-xl border border-stone-200 bg-white p-5 scroll-mt-24"
  >
    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-stone-900 text-white font-semibold text-sm">{step}</span>
        <h2 className="text-base font-semibold text-stone-900">{title}</h2>
        <DisclosureBadge label={status} tone={tone} />
      </div>
      {action}
    </div>
    {summary && <div className="mt-2">{summary}</div>}
    {children}
  </section>
);


export default HrV1Console;
